/**
 * Client fetch for AI disclosure copy (English local; other locales via callable cache).
 * Memory-only per page load. Falls back to English on timeout / failure / unsigned-in.
 */

import { getFunctions, httpsCallable } from "firebase/functions";
import {
  AI_DISCLOSURE_COPY,
  getAiDisclosureCopy,
} from "./aiDisclosureCopy";

const FETCH_TIMEOUT_MS = 3000;

/** @type {Map<string, Promise<{ copy: object, source: string, isTranslated: boolean }>>} */
const inFlight = new Map();
/** @type {Map<string, { copy: object, source: string, isTranslated: boolean }>} */
const memoryCache = new Map();

function normalizeLocale(locale) {
  if (!locale || typeof locale !== "string") return "en";
  const trimmed = locale.trim();
  if (!trimmed) return "en";
  if (trimmed.toLowerCase().startsWith("en")) return "en";
  return trimmed;
}

/**
 * @param {{ locale?: string, uid?: string | null }} opts
 * @returns {Promise<{ copy: object, source: "en" | "machine" | "human", isTranslated: boolean }>}
 */
export async function fetchAiDisclosureCopyForLocale({ locale, uid } = {}) {
  const key = normalizeLocale(locale);
  if (key === "en") {
    return { copy: AI_DISCLOSURE_COPY.en, source: "en", isTranslated: false };
  }
  if (!uid) {
    return { copy: AI_DISCLOSURE_COPY.en, source: "en", isTranslated: false };
  }
  if (memoryCache.has(key)) {
    return memoryCache.get(key);
  }
  if (inFlight.has(key)) {
    return inFlight.get(key);
  }

  const promise = (async () => {
    const callable = httpsCallable(getFunctions(), "getAiDisclosureCopy");
    const timeout = new Promise((_, reject) => {
      setTimeout(() => reject(new Error("ai-disclosure-timeout")), FETCH_TIMEOUT_MS);
    });
    try {
      const result = await Promise.race([callable({ locale: key }), timeout]);
      const data = result?.data || {};
      const copy = data.copy && typeof data.copy === "object" ? data.copy : null;
      if (!copy || typeof copy.headline !== "string") {
        throw new Error("invalid-ai-disclosure-copy");
      }
      const source = data.source === "human" ? "human" : "machine";
      const packed = { copy, source, isTranslated: true };
      memoryCache.set(key, packed);
      return packed;
    } catch (_err) {
      return { copy: AI_DISCLOSURE_COPY.en, source: "en", isTranslated: false };
    } finally {
      inFlight.delete(key);
    }
  })();

  inFlight.set(key, promise);
  return promise;
}

/** Test helper — clear memory caches. */
export function __resetAiDisclosureFetchCacheForTests() {
  inFlight.clear();
  memoryCache.clear();
}

export { getAiDisclosureCopy, AI_DISCLOSURE_COPY };
